import {
	Dialog,
	Toolbar
} from "client/Components";
import {
	EffectiveTheme,
	useDataInputDialog,
	useEffectiveTheme
} from "client/Hooks";
import {
	CultureResolvableMap,
	localize,
	resolveForCurrentCulture
} from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { ReactElement } from "react";
import styles from "./ExamplesDialog.module.less";
import { Theme } from "client/Api/Branding/UserTheme/userTheme";
import { IntegrationFlowKind } from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import enLightLiveTileTickets from "Content/Images/IntegrationBuilder/en-light-live-tile-tickets.png";
import enDarkLiveTileTickets from "Content/Images/IntegrationBuilder/en-dark-live-tile-tickets.png";
import nlLightLiveTileTickets from "Content/Images/IntegrationBuilder/nl-light-live-tile-tickets.png";
import nlDarkLiveTileTickets from "Content/Images/IntegrationBuilder/nl-dark-live-tile-tickets.png";
import enLightGlobalSearchTickets from "Content/Images/IntegrationBuilder/en-light-global-search-tickets.png";
import enDarkGlobalSearchTickets from "Content/Images/IntegrationBuilder/en-dark-global-search-tickets.png";
import nlLightGlobalSearchTickets from "Content/Images/IntegrationBuilder/nl-light-global-search-tickets.png";
import nlDarkGlobalSearchTickets from "Content/Images/IntegrationBuilder/nl-dark-global-search-tickets.png";

export type ExamplesDialogProps = {
	kind: IntegrationFlowKind;
	onClose: () => void;
};

export const ExamplesDialog = (props: ExamplesDialogProps): ReactElement => {
	const {
		kind,
		onClose
	} = props;

	const effectiveTheme: EffectiveTheme = useEffectiveTheme();
	const examples: ExamplesData = getExamplesForKind(kind);

	return (
		<Dialog
			size="long"
			trackingId="integration-builder-examples-dialog"
		>
			<Dialog.Header
				title={localize(examples.title)}
				onClose={onClose}
			/>
			<Dialog.Body>
				<div className={styles.section}>
					<div className={styles.description}>
						{examples.description.map(
							(x: LocalizationKey): ReactElement => (
								<p key={x}>{localize(x)}</p>
							)
						)}
					</div>
					<span>{localize("_ExamplesTitle")}</span>
					<ul className={styles.examples}>
						{examples.cards.map(
							(card: ExampleCardData): ReactElement => (
								<li
									key={card.title}
									className={styles.example}
								>
									<span className={styles.exampleTitle}>
										{localize(card.title)}
									</span>
									<img
										className={styles.image}
										src={resolveImage(card.images, effectiveTheme)}
										alt={localize(card.title)}
									/>
								</li>
							)
						)}
					</ul>
				</div>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Close")}
						onClick={onClose}
						trackingId="close"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
ExamplesDialog.displayName = "ExamplesDialog";

type ThemedImages = {
	light: string;
	dark: string;
};

type ExampleCardData = {
	title: LocalizationKey;
	images: CultureResolvableMap<ThemedImages>;
};

type ExamplesData = {
	title: LocalizationKey;
	description: readonly LocalizationKey[];
	cards: readonly ExampleCardData[];
};

function resolveImage(
	images: CultureResolvableMap<ThemedImages>,
	effectiveTheme: EffectiveTheme
): string {
	const themedImages: ThemedImages = resolveForCurrentCulture(images);

	switch (effectiveTheme) {
		case Theme.Dark:
			return themedImages.dark;
		case Theme.Light:
			return themedImages.light;
		default:
			never(effectiveTheme);
	}
}

function getExamplesForKind(kind: IntegrationFlowKind): ExamplesData {
	switch (kind) {
		case IntegrationFlowKind.General:
			return {
				title: "_GeneralIntegration",
				description: [
					"_UseForGeneralResults",
					"_CanBeUsedInGlobalSearchAndLiveTiles"
				],
				cards: [
					{
						title: "_LiveTileTickets",
						images: {
							standard: {
								light: enLightLiveTileTickets,
								dark: enDarkLiveTileTickets
							},
							nl: {
								light: nlLightLiveTileTickets,
								dark: nlDarkLiveTileTickets
							}
						}
					},
					{
						title: "_LiveTileGlobalSearchTickets",
						images: {
							standard: {
								light: enLightGlobalSearchTickets,
								dark: enDarkGlobalSearchTickets
							},
							nl: {
								light: nlLightGlobalSearchTickets,
								dark: nlDarkGlobalSearchTickets
							}
						}
					}
				]
			};
		default:
			never(kind);
	}
}

export function useExamplesDialog(): [
	element: ReactElement | null,
	open: (kind: IntegrationFlowKind) => Promise<TryResult<void>>
] {
	return useDataInputDialog<IntegrationFlowKind, void>(
		(kind, onClose) => (
			<ExamplesDialog
				kind={kind}
				onClose={onClose}
			/>
		)
	);
}
